import { BarChart3, Building2, TrendingUp, Cpu, Target, FileText, Newspaper } from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import { ColorVariant } from '../lib/colors';

/**
 * Interface for research type definitions with comprehensive styling support
 */
export interface ResearchType {
  id: string;
  name: string;
  icon: LucideIcon;
  description: string;
  colorVariant: ColorVariant;
  iconGradient: string;
  // Additional styling properties for comprehensive theming
  color: string;
  lightBg: string;
  darkBg: string;
  border: string;
  darkBorder: string;
  tagBg: string;
  darkTagBg: string;
  tagText: string;
  darkTagText: string;
  chevronColor: string;
}

/**
 * Research types aligned with backend configuration (from static-config-loader.ts)
 * These match the exact names expected by the Perplexity edge function
 */
export const RESEARCH_TYPES: ResearchType[] = [
  {
    id: 'Technology & Innovation',
    name: 'Technology & Innovation',
    icon: Cpu,
    description: 'Technology trends, technical specifications, and innovations',
    colorVariant: 'amber',
    iconGradient: 'from-orange-500 to-orange-600',
    color: 'bg-gradient-to-r from-orange-500 to-orange-600',
    lightBg: 'bg-orange-50',
    darkBg: 'dark:bg-orange-900/20',
    border: 'border-orange-300',
    darkBorder: 'dark:border-orange-800',
    tagBg: 'bg-orange-100',
    darkTagBg: 'dark:bg-orange-900/40',
    tagText: 'text-orange-700',
    darkTagText: 'dark:text-orange-300',
    chevronColor: 'text-orange-500'
  },
  {
    id: 'Business & Professional',
    name: 'Business & Professional',
    icon: Building2,
    description: 'Strategic business analysis with market data and competitive insights',
    colorVariant: 'blue',
    iconGradient: 'from-blue-500 to-blue-600',
    color: 'bg-gradient-to-r from-blue-500 to-blue-600',
    lightBg: 'bg-blue-50',
    darkBg: 'dark:bg-blue-900/20',
    border: 'border-blue-300',
    darkBorder: 'dark:border-blue-800',
    tagBg: 'bg-blue-100',
    darkTagBg: 'dark:bg-blue-900/40',
    tagText: 'text-blue-700',
    darkTagText: 'dark:text-blue-300',
    chevronColor: 'text-blue-500'
  },
  {
    id: 'Content & Creative',
    name: 'Content & Creative',
    icon: TrendingUp,
    description: 'Creative strategy insights with trend analysis and best practices',
    colorVariant: 'purple',
    iconGradient: 'from-purple-500 to-purple-600',
    color: 'bg-gradient-to-r from-purple-500 to-purple-600',
    lightBg: 'bg-purple-50',
    darkBg: 'dark:bg-purple-900/20',
    border: 'border-purple-300',
    darkBorder: 'dark:border-purple-800',
    tagBg: 'bg-purple-100',
    darkTagBg: 'dark:bg-purple-900/40',
    tagText: 'text-purple-700',
    darkTagText: 'dark:text-purple-300',
    chevronColor: 'text-purple-500'
  },
  {
    id: 'Personal Development',
    name: 'Personal Development',
    icon: Target,
    description: 'Evidence-based personal development guidance with practical steps',
    colorVariant: 'green',
    iconGradient: 'from-green-500 to-green-600',
    color: 'bg-gradient-to-r from-green-500 to-green-600',
    lightBg: 'bg-green-50',
    darkBg: 'dark:bg-green-900/20',
    border: 'border-green-300',
    darkBorder: 'dark:border-green-800',
    tagBg: 'bg-green-100',
    darkTagBg: 'dark:bg-green-900/40',
    tagText: 'text-green-700',
    darkTagText: 'dark:text-green-300',
    chevronColor: 'text-green-500'
  },
  {
    id: 'Academic & Learning',
    name: 'Academic & Learning',
    icon: FileText,
    description: 'Scholarly analysis with proper citations and peer-reviewed evidence',
    colorVariant: 'indigo',
    iconGradient: 'from-indigo-500 to-indigo-600',
    color: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
    lightBg: 'bg-indigo-50',
    darkBg: 'dark:bg-indigo-900/20',
    border: 'border-indigo-300',
    darkBorder: 'dark:border-indigo-800',
    tagBg: 'bg-indigo-100',
    darkTagBg: 'dark:bg-indigo-900/40',
    tagText: 'text-indigo-700',
    darkTagText: 'dark:text-indigo-300',
    chevronColor: 'text-indigo-500'
  },
  {
    id: 'Current Events & News',
    name: 'Current Events & News',
    icon: Newspaper,
    description: 'Balanced news analysis with multiple perspectives and impact assessment',
    colorVariant: 'red',
    iconGradient: 'from-red-500 to-red-600',
    color: 'bg-gradient-to-r from-red-500 to-red-600',
    lightBg: 'bg-red-50',
    darkBg: 'dark:bg-red-900/20',
    border: 'border-red-300',
    darkBorder: 'dark:border-red-800',
    tagBg: 'bg-red-100',
    darkTagBg: 'dark:bg-red-900/40',
    tagText: 'text-red-700',
    darkTagText: 'dark:text-red-300',
    chevronColor: 'text-red-500'
  }
];

/**
 * Legacy research type names mapping for backward compatibility
 * Maps old frontend names to new backend-aligned names
 */
export const LEGACY_RESEARCH_TYPE_MAPPING: Record<string, string> = {
  'Market Analysis': 'Business & Professional',
  'Business Analysis': 'Business & Professional',
  'Marketing Analysis': 'Content & Creative',
  'Technical Research': 'Technology & Innovation',
  'Competitive Research': 'Business & Professional',
  'Industry Report': 'Academic & Learning',
  'General Research': 'Business & Professional'
};

/**
 * Get a research type by ID, with fallback for legacy names
 * @param id The ID of the research type to retrieve
 * @returns The research type or undefined if not found
 */
export function getResearchTypeById(id: string): ResearchType | undefined {
  // First try direct match
  let researchType = RESEARCH_TYPES.find(type => type.id === id);
  
  // If not found, try legacy mapping
  if (!researchType && LEGACY_RESEARCH_TYPE_MAPPING[id]) {
    const mappedId = LEGACY_RESEARCH_TYPE_MAPPING[id];
    researchType = RESEARCH_TYPES.find(type => type.id === mappedId);
  }
  
  return researchType;
}

/**
 * Get all research type names for use in dropdowns/selectors
 * @returns Array of research type names
 */
export function getResearchTypeNames(): string[] {
  return RESEARCH_TYPES.map(type => type.name);
}

/**
 * Get the icon gradient for a research type
 * @param id The ID of the research type
 * @returns The icon gradient class string or a default if not found
 */
export function getResearchTypeGradient(id: string): string {
  const researchType = getResearchTypeById(id);
  return researchType?.iconGradient || 'from-blue-500 to-blue-600';
}

/**
 * Create a mapping from research type names to their configurations
 * This replaces the old researchTypeConfigs
 */
export const researchTypeConfigs = RESEARCH_TYPES.reduce((acc, type) => {
  acc[type.id] = type;
  return acc;
}, {} as Record<string, ResearchType>); 